import {KennethMustDie} from "../KennethMustDie";
import {KMD_SYMBOL_ANIMATIONS} from "../server/IKMDData";
import {KMDSound} from "../sound/KMDSound";
import {gsap} from "gsap";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";

export class KMDNearWinAnticipation extends PIXI.Container {

    private _fs: PIXI.spine.Spine; // type 0
    private _cfs: PIXI.spine.Spine; // type 1
    private _sfs: PIXI.spine.Spine; // type 2
    private _allItems: PIXI.spine.Spine[];
    private _fireHouse: PIXI.spine.Spine;
    private _anticipationStarted: boolean;
    private _anticipateFires: TimelineSprite[];
    private _xHFireLanding: TimelineSprite;
    private _xHFireLoop: TimelineSprite;
    private _xHStopCalled: boolean;

    constructor() {
        super();
        this.name = "KMDNearWinAnticipation";

        this.init();
        this._xHStopCalled = false;
    }

    public playAnticipation(reelId: number, rowId: number, type: number): void {
        const state: string = (reelId === (KennethMustDie.REELS_NUM - 1) ? KMD_SYMBOL_ANIMATIONS.HALF : KMD_SYMBOL_ANIMATIONS.NORMAL),
            item: PIXI.spine.Spine = this._allItems[type];

        item.position.set((reelId * KennethMustDie.SYMBOL_WIDTH) + KennethMustDie.SYMBOL_WIDTH / 2, KennethMustDie.MAIN_REELS_TOP + KennethMustDie.SYMBOL_HEIGHT * rowId);
        item.visible = true;
        item.updateTransform();
        item.skeleton.setToSetupPose();
        item.state.setAnimation(0, state, true);

        if (!this._anticipationStarted) {
            KennethMustDie.soundPlayer.playSound(KMDSound.SCATTER_ANTICIPATION, 1, Number.MAX_VALUE);
        }

        this.stopAllAnticipations(type);

        KennethMustDie.xHoleBlackBg.show(0);
        KennethMustDie.xHoleBlackBg.playFlicker();

        if (type === 2) {
            this.playHouseFire();
        }

        this._anticipationStarted = true;
    }

    public stopReelAnticipations(type: number): void {
        for (let ctr: number = 0; ctr < this._allItems.length; ctr++) {
            if (ctr !== type) {
                this._allItems[ctr].visible = false;
                this._allItems[ctr].state.clearTracks();
            }
        }
    }

    public stopAllAnticipations(type: number): void {
        this.stopReelAnticipations(type);

        if (type === -1) {
            KennethMustDie.xHoleBlackBg.stopFlicker();

            if (!KennethMustDie.model.isSFSTriggered) {
                this.stopFire();
            }
        }

        this._anticipationStarted = false;
    }

    public playHouseFire(): void {
        this._fireHouse.visible = true;
        this._fireHouse.updateTransform();
        this._fireHouse.state.setAnimation(0, "animation", true);

        // #485
        this.playXHAnticipateFireAnimation(0);
    }

    public stopFire(): void {
        this._fireHouse.visible = false;
        this._fireHouse.state.clearTracks();

        KennethMustDie.model.isSFSTriggered = false;

        this.stopXHAnticipationFire();
    }

    // #485
    public playAnticipateFireAnimation(reelId: number, rowId: number, resizeLevel: number): gsap.core.Timeline {
        const landing: TimelineSprite = new TimelineSprite(KennethMustDie.assetLoader.getTextures("scatter_landing_loop/landing/landing_")),
            loop: TimelineSprite = new TimelineSprite(KennethMustDie.assetLoader.getTextures("scatter_landing_loop/loop/fire_frame_loop_")),
            tl: gsap.core.Timeline = gsap.timeline(),
            x: number = ((reelId * KennethMustDie.SYMBOL_WIDTH) + (KennethMustDie.SYMBOL_WIDTH / 2)),
            yScale: number = (resizeLevel === 0 ? 1 : 0.67);
        let y: number = (rowId * KennethMustDie.SYMBOL_HEIGHT);

        if (resizeLevel === 1) {
            const yOffset: number[] = [-25, -75, -125];
            y += yOffset[rowId - 1];
        }

        if (this._anticipateFires[reelId]) {
            this._anticipateFires[reelId].scale.y = yScale;
            this._anticipateFires[reelId].y = y;

            return tl;
        }

        landing.position.set(x - 2.8861, y + 21.164); // #539
        landing.anchor.set(0.5);
        landing.scale.set(1, yScale);

        loop.position.set(x, y);
        loop.anchor.set(0.5);
        loop.scale.set(1, yScale);
        loop.alpha = 0;

        this._anticipateFires[reelId] = loop;

        tl.add([
            () => {
                this.addChild(landing, loop);

                loop.getAnimation(undefined, undefined, () => {
                    if (loop.parent && loop.visible) {
                        loop.playLoop();
                    }
                });
            },
            <gsap.core.Timeline><unknown>landing.getAnimation()
        ], 0);
        tl.to(loop, {
            duration: 3 / 30,
            alpha: 1
        }, 6 / 30);

        tl.add(() => {
            this.removeChild(landing);
        });

        return tl;
    }

    // #485
    public resizeAnticipationFire(reelId: number, yOffset: number): void {
        const val: TimelineSprite = this._anticipateFires[reelId];
        if (val) {
            val.scale.y = 0.67;
            val.y += yOffset;
        }
    }

    // #485
    public playXHAnticipateFireAnimation(resizeLevel: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            yScale: number = (resizeLevel === 0 ? 1 : 0.67);

        this._xHStopCalled = false;

        if (this._xHFireLoop.visible) {
            this._xHFireLoop.scale.y = yScale;
            this._xHFireLoop.y = (resizeLevel === 0 ? 300 : 225);

            return tl;
        }

        this._xHFireLanding.y = (resizeLevel === 0 ? 300 : 225) + 21.164; // #539
        this._xHFireLanding.scale.set(1, yScale);

        this._xHFireLoop.y = (resizeLevel === 0 ? 300 : 225);
        this._xHFireLoop.scale.set(1, yScale);
        this._xHFireLoop.alpha = 0;

        tl.add([
            () => {
                if (!this._xHStopCalled) {
                    this._xHStopCalled = true;

                    this._xHFireLanding.show();
                    this._xHFireLoop.show();

                    this.addChild(this._xHFireLanding, this._xHFireLoop);

                    this._xHFireLoop.getAnimation(undefined, undefined, () => {
                        if (this._xHFireLoop.parent && this._xHFireLoop.visible) {
                            this._xHFireLoop.playLoop();
                        }
                    });
                }
            },
            <gsap.core.Timeline><unknown>this._xHFireLanding.getAnimation()
        ], 0);
        tl.to(this._xHFireLoop, {
            duration: 3 / 30,
            alpha: 1
        }, 6 / 30);

        tl.add(() => {
            this._xHFireLanding.hide();
            this.removeChild(this._xHFireLanding);
        });

        return tl;
    }

    // #485
    public resizeXHAnticipationFire(): void {
        const val: TimelineSprite = this._xHFireLoop;
        if (val.visible) {
            val.scale.y = 0.67;
            val.y = 225;
        }
    }

    // #485
    public stopAnticipationFire(reelId: number): void {
        const val: TimelineSprite = this._anticipateFires[reelId];
        if (val) {
            val.stopLoop();
            val.hide();
            this.removeChild(val);

            delete this._anticipateFires[reelId];
        }
    }

    // #485
    public stopXHAnticipationFire(): void {
        this._xHStopCalled = true;

        if (this._xHFireLoop.visible) {
            this._xHFireLoop.stopLoop();
            this._xHFireLoop.hide();
            this.removeChild(this._xHFireLoop);
        }
    }

    private init(): void {
        const anticipationNameHelper: string = "Anticipation";

        this._fs = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("fs" + anticipationNameHelper));
        this._fs.visible = false;

        this._cfs = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("cfs" + anticipationNameHelper));
        this._cfs.visible = false;

        this._sfs = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("sfs" + anticipationNameHelper));
        this._sfs.visible = false;

        this._fireHouse = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("fireHouse"));
        this._fireHouse.visible = false;
        this._fireHouse.position.set(360, 300);

        this._allItems = [this._fs, this._cfs, this._sfs];

        this.addChild(this._fs, this._cfs, this._sfs, this._fireHouse);

        this._anticipationStarted = false;

        this._anticipateFires = [];

        const x: number = ((2 * KennethMustDie.SYMBOL_WIDTH) + (KennethMustDie.SYMBOL_WIDTH / 2));

        this._xHFireLanding = new TimelineSprite(KennethMustDie.assetLoader.getTextures("scatter_landing_loop/landing/landing_"));
        this._xHFireLanding.hide();
        this._xHFireLanding.position.set(x - 2.8861, 0); // #539
        this._xHFireLanding.anchor.set(0.5);

        this._xHFireLoop = new TimelineSprite(KennethMustDie.assetLoader.getTextures("scatter_landing_loop/loop/fire_frame_loop_"));
        this._xHFireLoop.hide();
        this._xHFireLoop.position.set(x, 0);
        this._xHFireLoop.anchor.set(0.5);
    }
}